import type {
	ClassifierApi,
	ClassifierContext,
	ClassifierModel,
	ClassifierOptions,
	ClassifierResult,
	RetryCallbacks,
	RetryPolicy,
} from "@bastani/pi-ai";
import type { Api, Model, Usage } from "@bastani/pi-ai/compat";
import type { StreamFn, ThinkingLevel } from "@earendil-works/pi-agent-core";
import type { SessionEntry } from "../session-manager-types.ts";
import { planClassifierRanges } from "./classifier-compaction.js";
import { getKeptTailTokenEstimate, hasKeptTailTokenEstimate } from "./compaction-boundary.js";
import type {
	BorrowedPlanner,
	CompactedTranscript,
	CompactionPlannerModel,
	CompactionUrgency,
	PlannerAuth,
	RawLineRange,
	VerbatimCompactionPreparation,
} from "./compaction-types.js";
import { MIN_COMPACTABLE_REGION_LINES } from "./compaction-types.js";
import { reconstructCompactedTranscript, validateDeletedRanges } from "./deleted-ranges.js";
import {
	type BorrowFallbackPlanner,
	createFallbackPlannerBorrower,
	type FallbackPlannerContext,
	plannerAttemptKey,
} from "./fallback-planner.js";
import type { CompactionModelSelection } from "./model-resolver.js";
import { planMorphRanges } from "./morph-compaction.js";
import { runPiSummaryFallback } from "./pi-summary-fallback.js";
import type { TerminalPlannerOutcome } from "./planner-outcome.js";
import {
	MALFORMED_OUTPUT_MESSAGE,
	NO_USABLE_RANGES_MESSAGE,
	planDeletedLineRanges,
	RangePlanError,
	resolvePlannerRequest,
} from "./range-planner.js";
import { writeMorphDiagnosticSidecar, writeSuccessDiagnosticSidecar } from "./range-planner-diagnostics.js";
import { nextTrimOffset, rebaseTrimmedRanges, trimRegionHead } from "./region-trimming.js";
import { widenToWholeContextStats } from "./whole-context-stats.js";

export interface CompactionPlanOptions {
	streamFn: StreamFn;
	/** Absolute path of the persisted session file. Undefined for in-memory sessions. */
	sessionFilePath?: string;
	retry?: RetryPolicy;
	callbacks?: RetryCallbacks;
}

/** One compaction run's request. Urgency is required; there is no default. */
export interface CompactionRunRequest extends CompactionPlanOptions {
	/** Per-model credentials; a borrowed candidate uses its own, never the session model's. */
	resolveAuth: (model: Model<Api>) => Promise<PlannerAuth | undefined>;
	signal?: AbortSignal;
	/** Inherited by the planner and never modified across attempts. */
	thinkingLevel: ThinkingLevel | undefined;
	urgency: CompactionUrgency;
	/** Configured fallback candidates. Omitted means borrowing is impossible. */
	fallback?: FallbackPlannerContext;
	compactionModel?: CompactionModelSelection;
	resolveMorphApiKey?: () => Promise<string | undefined>;
	morphFetchFn?: typeof fetch;
	classify?: (
		model: ClassifierModel<ClassifierApi>,
		context: ClassifierContext,
		options?: ClassifierOptions,
	) => Promise<ClassifierResult>;
	summaryEntries?: SessionEntry[];
	cwd?: string;
}

export type CompactionRungResult = CompactedTranscript & {
	rung: "planned" | "fresh";
	/** Present only when a borrowed fallback model ranked the lines. */
	plannerModel?: CompactionPlannerModel;
	/** False only when the fresh rung had to drop the protected tail. */
	keptTail: boolean;
	/** Aggregate usage across every planner request and retry in this run. */
	usage?: Usage;
	backend?: "planner" | "classifier" | "morph" | "summary";
	model?: string;
	summary?: { readFiles: string[]; modifiedFiles: string[] };
	summaryFirstKeptEntryId?: string;
	summaryTokensBefore?: number;
};

/** Runner-internal fresh-window build: the transcript plus the tail decision. */
interface FreshWindowBuild {
	transcript: CompactedTranscript;
	keptTail: boolean;
}

function addUsage(total: Usage | undefined, usage: Usage): Usage {
	const cacheWrite1h = (total?.cacheWrite1h ?? 0) + (usage.cacheWrite1h ?? 0);
	const reasoning = (total?.reasoning ?? 0) + (usage.reasoning ?? 0);
	return {
		input: (total?.input ?? 0) + usage.input,
		output: (total?.output ?? 0) + usage.output,
		cacheRead: (total?.cacheRead ?? 0) + usage.cacheRead,
		cacheWrite: (total?.cacheWrite ?? 0) + usage.cacheWrite,
		...(total?.cacheWrite1h !== undefined || usage.cacheWrite1h !== undefined ? { cacheWrite1h } : {}),
		...(total?.reasoning !== undefined || usage.reasoning !== undefined ? { reasoning } : {}),
		totalTokens: (total?.totalTokens ?? 0) + usage.totalTokens,
		cost: {
			input: (total?.cost.input ?? 0) + usage.cost.input,
			output: (total?.cost.output ?? 0) + usage.cost.output,
			cacheRead: (total?.cost.cacheRead ?? 0) + usage.cost.cacheRead,
			cacheWrite: (total?.cost.cacheWrite ?? 0) + usage.cost.cacheWrite,
			total: (total?.cost.total ?? 0) + usage.cost.total,
			totalFromProvider:
				(total === undefined || total.cost.totalFromProvider === true) && usage.cost.totalFromProvider === true,
		},
	};
}

/**
 * Calculate the single global line threshold from the prepared setting.
 *
 * Protected lines count *against* this target rather than raising it: keeping 40% under a 0.5
 * ratio leaves the remaining 60% to compress harder to hit the same total. That keeps the
 * compression ratio a real bound on output size, so protection can never enlarge the result.
 *
 * This is the untrimmed target. Overflow trimming does NOT reuse it:
 * `planWithTrimming` recomputes the threshold against each trimmed view, so the
 * planner is always asked to keep a proportion of the lines it actually
 * received rather than a proportion of a region it never saw.
 *
 * Retained as an exported helper for tests and consumers that need the
 * pre-trim target; production planning goes through `planWithTrimming`.
 */
export function targetKeepLines(preparation: VerbatimCompactionPreparation): number {
	const region = preparation.region;
	return Math.round(region.lines.length * preparation.parameters.compression_ratio);
}

function hardInputLimitFor(model: Model<Api>): number {
	return model.contextWindow > 0 ? model.contextWindow : Number.POSITIVE_INFINITY;
}

/**
 * Widen region-level transcript stats to whole-context stats (#2052).
 *
 * `result.stats` must be the symmetric region-only stats produced by
 * `reconstructCompactedTranscript`; this adds the independently estimated kept
 * tail to the heuristic before count on both sides — and, only when the tail is
 * kept, to the after count — via the shared `widenToWholeContextStats`. The
 * authoritative `preparation.tokensBefore` is deliberately absent from this
 * comparison and travels separately for budgeting/display.
 */
function withWholeContextStats(
	result: CompactedTranscript,
	preparation: VerbatimCompactionPreparation,
	keptTail: boolean,
): CompactedTranscript {
	return {
		...result,
		stats: widenToWholeContextStats(result.stats, getKeptTailTokenEstimate(preparation), keptTail),
	};
}

/**
 * Build a fresh context window and the tail decision that goes with it.
 *
 * Private on purpose: `keptTail` is runner bookkeeping that persistence needs in
 * order to write `firstKeptEntryId: null` when the protected tail is dropped. It
 * is deliberately absent from the public door's return value, which is exactly a
 * `CompactedTranscript`.
 */
function buildFreshTranscript(preparation: VerbatimCompactionPreparation): CompactedTranscript {
	const region = preparation.region;
	const whole: RawLineRange[] = region.lines.length > 0 ? [{ start: 1, end: region.lines.length }] : [];
	return reconstructCompactedTranscript(region, validateDeletedRanges(whole, region));
}

function buildFreshContextWindow(preparation: VerbatimCompactionPreparation, hardInputLimit: number): FreshWindowBuild {
	const limit = Number.isFinite(hardInputLimit) && hardInputLimit > 0 ? hardInputLimit : Number.POSITIVE_INFINITY;
	// The rule is "tail under the limit ⇒ tail kept", so the comparison is the
	// tail alone. Explicit protected lines are a hard floor that survives either
	// way, and counting the reconstructed protected/marker text here would drop a
	// tail that fits. A rebuilt context that still cannot be sent is the post-tool
	// gate's problem, not a reason for this rung to discard more.
	// A kept tail with no registered estimate (a directly constructed
	// preparation) is dropped conservatively rather than assumed to fit (P1 #3010).
	const keptTail =
		preparation.keptTailMessageCount === 0 ||
		(hasKeptTailTokenEstimate(preparation) && getKeptTailTokenEstimate(preparation) <= limit);
	return { transcript: buildFreshTranscript(preparation), keptTail };
}

/**
 * Discard every compactable line and start a fresh context window.
 *
 * TOTAL. No provider, no credentials, no network, no signal, no failure mode,
 * no `Promise`. Port of codex `compact_token_budget` →
 * `Session::start_new_context_window`. Atomic's system prompt, context files,
 * and skills are rebuilt per request and were never in the transcript, so they
 * survive automatically.
 *
 * The whole region is emitted as one deletion range and handed to the unchanged
 * `validateDeletedRanges` → `reconstructCompactedTranscript` path, which splits
 * around protected spans and folds prior markers.
 *
 * Returns exactly a `CompactedTranscript` — `text`, `ranges`, `stats` and
 * nothing else. Whether the `preserve_recent` tail survives is a persistence
 * concern and travels on `CompactionRungResult.keptTail`, not on this door.
 */
export function startNewContextWindow(preparation: VerbatimCompactionPreparation): CompactedTranscript {
	return buildFreshTranscript(preparation);
}

function plannedResult(
	preparation: VerbatimCompactionPreparation,
	ranges: RawLineRange[],
	plannerModel: CompactionPlannerModel | undefined,
	usage: Usage | undefined,
	model: string,
): CompactionRungResult {
	const reconstructed = reconstructCompactedTranscript(
		preparation.region,
		validateDeletedRanges(ranges, preparation.region),
	);
	return {
		...withWholeContextStats(reconstructed, preparation, true),
		rung: "planned",
		backend: "planner",
		model,
		...(plannerModel ? { plannerModel } : {}),
		keptTail: true,
		...(usage ? { usage } : {}),
	};
}

function borrowedIdentity(planner: BorrowedPlanner): CompactionPlannerModel {
	return {
		provider: planner.model.provider,
		id: planner.model.id,
		...(planner.budget.reasoning ? { thinkingLevel: planner.budget.reasoning } : {}),
	};
}

function terminalError(outcome: TerminalPlannerOutcome | undefined): RangePlanError {
	if (!outcome) return new RangePlanError(MALFORMED_OUTPUT_MESSAGE, 1, "", false);
	if (outcome.kind === "rateLimited") {
		return new RangePlanError(outcome.message, 1, "", false, outcome.diagnosticPath, outcome);
	}
	if (outcome.kind === "overflowed") {
		return new RangePlanError(
			"Compaction range planning exceeded the model context window",
			1,
			"",
			true,
			outcome.diagnosticPath,
			outcome,
		);
	}
	if (outcome.kind === "providerError" || outcome.kind === "policyRefusal") {
		return new RangePlanError(outcome.message, 1, "", false, outcome.diagnosticPath, outcome);
	}
	const message = outcome.category === "malformed_output" ? MALFORMED_OUTPUT_MESSAGE : NO_USABLE_RANGES_MESSAGE;
	return new RangePlanError(message, 1, outcome.excerpt, false, outcome.diagnosticPath, outcome);
}

type PlannerAttempt =
	| { kind: "planned"; ranges: RawLineRange[] }
	| { kind: "terminal"; outcome: TerminalPlannerOutcome };

/**
 * Run one planner model, retrying the same model after oldest-first input
 * trimming when the request itself overflows the context window.
 *
 * Trimming continues until no strictly smaller view exists; `nextTrimOffset`
 * halves the remaining suffix, so the walk is finite. Only then is `overflowed`
 * terminal and the ladder advances.
 *
 * Each view gets its own keep target from `compression_ratio`. Reusing the
 * original whole-preparation target is what asked a halved 20-line region to
 * keep 10 of its 10 visible lines — a request for zero deletions, whose obedient
 * empty answer then hits the non-negotiable zero-range rejection. Extra
 * reduction still comes only from the head the runner withheld.
 */
async function planWithTrimming(
	preparation: VerbatimCompactionPreparation,
	planner: BorrowedPlanner,
	options: CompactionPlanOptions & { signal?: AbortSignal },
): Promise<PlannerAttempt> {
	let offset = 0;
	for (;;) {
		const region = offset === 0 ? preparation.region : trimRegionHead(preparation.region, offset);
		if (!region) return { kind: "terminal", outcome: { kind: "overflowed" } };
		const keepTarget = Math.max(
			region.protectedLineNumbers?.size ?? 0,
			Math.round(region.lines.length * preparation.parameters.compression_ratio),
		);
		const outcome = await planDeletedLineRanges(region, preparation.parameters, planner, keepTarget, options);
		if (outcome.kind === "ranked" || outcome.kind === "recovered") {
			if (offset === 0) return { kind: "planned", ranges: outcome.ranges };
			const validated = validateDeletedRanges(outcome.ranges, region);
			return { kind: "planned", ranges: rebaseTrimmedRanges(validated, offset) };
		}
		if (outcome.kind !== "overflowed") return { kind: "terminal", outcome };
		if (options.signal?.aborted) throw new Error("Compaction cancelled");
		const next = nextTrimOffset(preparation.region, offset);
		if (next === undefined) return { kind: "terminal", outcome };
		offset = next;
	}
}

const MISSING_AUTH_MESSAGE = "Compaction provider authentication is unavailable";

/**
 * A runner-local terminal cause with no provider attempt behind it.
 *
 * Missing credentials are not a provider outcome — no request was made — so no
 * synthetic response is fabricated and no diagnostic sidecar is written. It is
 * carried as `providerError` purely so the ladder can advance past it.
 */
function authFailureOutcome(error: unknown): TerminalPlannerOutcome {
	const message = error instanceof Error ? error.message : error === undefined ? MISSING_AUTH_MESSAGE : String(error);
	return { kind: "providerError", message: message || MISSING_AUTH_MESSAGE };
}

/** Resolve one model's credentials, normalizing a rejection and `undefined` alike. */
async function resolvePlannerAuth(
	resolveAuth: CompactionRunRequest["resolveAuth"],
	model: Model<Api>,
): Promise<{ auth: PlannerAuth } | { failure: TerminalPlannerOutcome }> {
	try {
		const auth = await resolveAuth(model);
		return auth ? { auth } : { failure: authFailureOutcome(undefined) };
	} catch (error) {
		return { failure: authFailureOutcome(error) };
	}
}

function freshResult(
	preparation: VerbatimCompactionPreparation,
	hardInputLimit: number,
	usage?: Usage,
): CompactionRungResult {
	const fresh = buildFreshContextWindow(preparation, hardInputLimit);
	return {
		...withWholeContextStats(fresh.transcript, preparation, fresh.keptTail),
		rung: "fresh",
		keptTail: fresh.keptTail,
		...(usage ? { usage } : {}),
	};
}

/**
 * Produce one compacted transcript, tagged with the rung that produced it.
 *
 * The ladder is: the session model, then each configured fallback model
 * borrowed for one planner request, then — only under `load_bearing` urgency —
 * a fresh context window. A manual `/compact` runs at `recoverable` urgency and
 * therefore cannot reach the context-destroying rung; it fails honestly instead.
 *
 * Unavailable credentials are ladder control flow, not an early throw: a
 * borrowed candidate with its own working credentials can still rank the lines,
 * and a load-bearing caller still reaches the credential-free fresh rung.
 */
export async function runVerbatimCompaction(
	preparation: VerbatimCompactionPreparation,
	model: Model<Api>,
	request: CompactionRunRequest,
): Promise<CompactionRungResult> {
	const signal = request.signal;
	if (signal?.aborted) throw new Error("Compaction cancelled");
	let plannerUsage: Usage | undefined;
	const plannerOptions = {
		streamFn: request.streamFn,
		sessionFilePath: request.sessionFilePath,
		retry: request.retry,
		callbacks: request.callbacks,
		signal,
		onUsage: (usage: Usage) => {
			plannerUsage = addUsage(plannerUsage, usage);
		},
	};
	const hardInputLimit = hardInputLimitFor(model);

	// A region below the planner minimum cannot be made rankable by changing
	// models, so no planner is invoked. Only a load-bearing caller can reach a
	// preparation this small; recoverable callers were refused before the runner.
	if (preparation.region.lines.length < MIN_COMPACTABLE_REGION_LINES) {
		if (request.urgency !== "load_bearing") throw new RangePlanError(NO_USABLE_RANGES_MESSAGE, 0, "", false);
		return freshResult(preparation, hardInputLimit);
	}

	const selection = request.compactionModel ?? { kind: "chat", fullId: `${model.provider}/${model.id}`, model };
	const attempted = new Set<string>();
	// One borrower per run owns the monotonic cursor, so the configured list is
	// walked exactly once no matter how many terminal outcomes occur.
	const borrow: BorrowFallbackPlanner | undefined = request.fallback
		? createFallbackPlannerBorrower({ ...request.fallback, selectedModelId: selection.fullId })
		: undefined;
	let backendFailure: TerminalPlannerOutcome | undefined;
	if (selection.kind === "classifier") {
		try {
			const classify = request.classify;
			if (!classify) throw new Error("Compaction classifier is unavailable");
			const ranges = await planClassifierRanges(
				preparation.region,
				preparation.parameters,
				(context) => classify(selection.model, context, { signal }),
				plannerOptions,
			);
			return {
				...plannedResult(preparation, ranges, undefined, plannerUsage, selection.fullId),
				backend: "classifier",
			};
		} catch (error) {
			if (signal?.aborted) throw new Error("Compaction cancelled");
			backendFailure = { kind: "providerError", message: error instanceof Error ? error.message : String(error) };
		}
	}
	if (selection.kind === "morph") {
		try {
			const ranges = await planMorphRanges(preparation.region, preparation.parameters, {
				apiKey: await request.resolveMorphApiKey?.(),
				signal,
				fetchFn: request.morphFetchFn,
				onDiagnostics: (diagnostics) => writeMorphDiagnosticSidecar(request.sessionFilePath, diagnostics),
			});
			return {
				...plannedResult(preparation, ranges, undefined, undefined, selection.fullId),
				backend: "morph",
			};
		} catch (error) {
			if (signal?.aborted) throw new Error("Compaction cancelled");
			backendFailure = { kind: "providerError", message: error instanceof Error ? error.message : String(error) };
		}
	}
	const primaryModel = selection.kind === "chat" ? selection.model : undefined;
	const primaryBudget = resolvePlannerRequest(primaryModel ?? model, request.thinkingLevel);
	const primaryAuth = primaryModel
		? await resolvePlannerAuth(request.resolveAuth, primaryModel)
		: {
				failure: {
					kind: "providerError" as const,
					message:
						backendFailure?.kind === "providerError"
							? backendFailure.message
							: `Compaction backend ${selection.kind} is not yet available`,
				},
			};
	let lastTerminal: TerminalPlannerOutcome | undefined = "failure" in primaryAuth ? primaryAuth.failure : undefined;
	let borrowed = primaryModel !== model;
	let planner: BorrowedPlanner | undefined;

	if (primaryModel && "auth" in primaryAuth) {
		planner = { model: primaryModel, budget: primaryBudget, auth: primaryAuth.auth };
	} else {
		if (primaryModel) attempted.add(plannerAttemptKey({ model: primaryModel, budget: primaryBudget, auth: {} }));
		planner = borrow ? await borrow(attempted, request.resolveAuth) : undefined;
		borrowed = planner !== undefined;
	}

	while (planner) {
		const attempt = await planWithTrimming(preparation, planner, plannerOptions);
		if (signal?.aborted) throw new Error("Compaction cancelled");
		if (attempt.kind === "planned") {
			const plannerModel = borrowed ? borrowedIdentity(planner) : undefined;
			if (plannerModel) {
				// Best-effort: the durable entry already records the borrowed model;
				// a sidecar write failure never affects compaction success.
				writeSuccessDiagnosticSidecar({
					sessionFilePath: request.sessionFilePath,
					model: planner.model,
					successCategory: "borrowed_planner",
					...(plannerModel.thinkingLevel === undefined ? {} : { thinkingLevel: plannerModel.thinkingLevel }),
				});
			}
			return plannedResult(
				preparation,
				attempt.ranges,
				plannerModel,
				plannerUsage,
				`${planner.model.provider}/${planner.model.id}`,
			);
		}
		lastTerminal = attempt.outcome;
		if (attempt.outcome.kind === "policyRefusal" && request.summaryEntries) {
			try {
				const summary = await runPiSummaryFallback(request.summaryEntries, preparation.settings, planner, {
					...plannerOptions,
					cwd: request.cwd,
				});
				return {
					text: summary.summary,
					ranges: [],
					keptRanges: [],
					stats: {
						linesBefore: preparation.region.lines.length,
						linesDeleted: 0,
						linesKept: summary.summary.split("\n").length,
						rangeCount: 0,
						tokensBefore: summary.tokensBefore,
						tokensAfter: summary.tokensAfter,
						percentReduction:
							summary.tokensBefore > 0 ? 100 * (1 - summary.tokensAfter / summary.tokensBefore) : 0,
					},
					rung: "planned",
					keptTail: true,
					backend: "summary",
					model: `${planner.model.provider}/${planner.model.id}`,
					summary: { readFiles: summary.readFiles, modifiedFiles: summary.modifiedFiles },
					summaryFirstKeptEntryId: summary.firstKeptEntryId,
					summaryTokensBefore: summary.tokensBefore,
					...(plannerUsage ? { usage: plannerUsage } : {}),
				};
			} catch (error) {
				if (signal?.aborted) throw new Error("Compaction cancelled");
				lastTerminal = { kind: "providerError", message: error instanceof Error ? error.message : String(error) };
			}
		}
		attempted.add(plannerAttemptKey(planner));
		planner = borrow ? await borrow(attempted, request.resolveAuth) : undefined;
		borrowed = planner !== undefined;
		if (signal?.aborted) throw new Error("Compaction cancelled");
	}

	if (request.urgency !== "load_bearing") throw terminalError(lastTerminal);
	return freshResult(preparation, hardInputLimit, plannerUsage);
}
